import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Fade,
} from "@mui/material";
import {
  DashboardRounded as DashboardIcon,
  StorageRounded as Storage,
  SettingsRounded as Settings,
  LogoutRounded as Logout,
  MenuRounded as MenuIcon,
  ShieldRounded as Shield,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { Kicker, Rule, Glow, SpotlightCard } from "../theme/scala";
import { BRAND, GRADIENT, LINE, EASE, FONT } from "../theme/brand";

const drawerWidth = 268;

// Panelin kabuğu. Ana sitedeki gezinme dili buraya taşındı: mono sıra numarası,
// ince gradyan çizgili bölüm etiketi (kicker), saç teli ayırıcılar ve aktif
// ögede tek renk ailesinde kalan cyan→azure vurgu çizgisi.
const MENU = [
  { text: "Genel Bakış", icon: <DashboardIcon />, path: "/", index: "01" },
  { text: "Savunma", icon: <Shield />, path: "/defense", index: "02" },
  { text: "Veritabanları", icon: <Storage />, path: "/databases", index: "03" },
  { text: "Ayarlar", icon: <Settings />, path: "/settings", index: "04" },
];

export default function Layout({ children }) {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", position: "relative" }}>
      {/* Kenar çubuğunun tepesindeki marka ışığı */}
      <Glow tone="cyan" size={260} sx={{ top: -120, left: -80, opacity: 0.5 }} />

      {/* ── Marka ── */}
      <Box sx={{ px: 3, pt: 3.5, pb: 2.5, display: "flex", alignItems: "center", gap: 1.5, position: "relative" }}>
        <Box
          component="img"
          src="/logo.png"
          alt="Visione"
          sx={{ width: 38, height: 38, objectFit: "cover", borderRadius: "10px" }}
        />
        <Box>
          <Typography sx={{ fontSize: "1.15rem", fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1.1 }}>
            Visione<Box component="span" sx={{ color: BRAND.ember }}>.</Box>
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT.mono,
              fontSize: "0.5625rem",
              letterSpacing: "0.24em",
              color: BRAND.inkFaint,
            }}
          >
            SCALAVISIONE
          </Typography>
        </Box>
      </Box>

      <Rule sx={{ opacity: 0.7 }} />

      {/* ── Gezinme ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1.5 }}>
        <Kicker>Kontrol</Kicker>
      </Box>

      <List sx={{ px: 1.5, flexGrow: 1, py: 0 }}>
        {MENU.map((item) => {
          const active = location.pathname === item.path;
          return (
            <ListItemButton
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              sx={{
                mb: 0.5,
                py: 1.05,
                px: 1.75,
                borderRadius: "12px",
                position: "relative",
                color: active ? BRAND.ink : BRAND.inkDim,
                backgroundColor: active ? "rgba(29,111,242,0.14)" : "transparent",
                transition: `all 0.3s ${EASE.outExpo}`,
                "&:hover": {
                  backgroundColor: active ? "rgba(29,111,242,0.20)" : "rgba(234,240,248,0.04)",
                  color: BRAND.ink,
                  transform: "translateX(3px)",
                },
                // Aktif ögenin solundaki marka çizgisi
                "&::before": active
                  ? {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: "18%",
                      height: "64%",
                      width: 2,
                      borderRadius: "0 3px 3px 0",
                      background: GRADIENT.brand,
                      boxShadow: `0 0 10px ${BRAND.cyan}`,
                    }
                  : {},
              }}
            >
              <Typography
                aria-hidden
                sx={{
                  fontFamily: FONT.mono,
                  fontSize: "0.625rem",
                  letterSpacing: "0.1em",
                  color: active ? BRAND.cyan : BRAND.inkFaint,
                  mr: 1.75,
                  transition: `color 0.3s ${EASE.outExpo}`,
                }}
              >
                {item.index}
              </Typography>
              <ListItemIcon sx={{ color: "inherit", minWidth: 34 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: active ? 600 : 500,
                  fontSize: "0.9rem",
                  letterSpacing: "-0.015em",
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {/* ── Oturum ── */}
      <Box sx={{ p: 2 }}>
        <SpotlightCard sx={{ borderRadius: "14px" }}>
          <Box
            sx={{
              p: 2,
              borderRadius: "14px",
              border: `1px solid ${LINE.hairline}`,
              bgcolor: "rgba(234,240,248,0.03)",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "10px",
                  background: GRADIENT.brand,
                  display: "grid",
                  placeItems: "center",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "#03070f",
                  fontFamily: FONT.mono,
                }}
              >
                SV
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, letterSpacing: "-0.02em" }}>
                  Yönetici
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT.mono,
                    fontSize: "0.625rem",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: BRAND.mint,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      bgcolor: BRAND.mint,
                      boxShadow: `0 0 8px ${BRAND.mint}`,
                    }}
                  />
                  Bağlı
                </Typography>
              </Box>
            </Box>

            <Rule sx={{ my: 1.75 }} />

            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: "10px",
                py: 0.75,
                px: 1,
                color: BRAND.inkDim,
                transition: `all 0.25s ${EASE.outExpo}`,
                "&:hover": { bgcolor: "rgba(255,107,129,0.10)", color: BRAND.rose },
              }}
            >
              <ListItemIcon sx={{ color: "inherit", minWidth: 32 }}>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Oturumu Kapat"
                primaryTypographyProps={{ fontSize: "0.8125rem", fontWeight: 600 }}
              />
            </ListItemButton>
          </Box>
        </SpotlightCard>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* ── Üst bar ── */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: scrolled ? "rgba(5, 7, 12, 0.72)" : "transparent",
          backdropFilter: scrolled ? "blur(20px) saturate(130%)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(20px) saturate(130%)" : "none",
          borderBottom: scrolled ? `1px solid ${LINE.hairline}` : "1px solid transparent",
          transition: `all 0.35s ${EASE.outExpo}`,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", minHeight: { xs: 64, sm: 76 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { sm: "none" },
              border: `1px solid ${LINE.hairline}`,
              bgcolor: "rgba(234,240,248,0.03)",
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          <Typography
            sx={{
              display: { xs: "none", sm: "block" },
              fontFamily: FONT.mono,
              fontSize: "0.6875rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: BRAND.inkFaint,
            }}
          >
            v1.1 · Ağ ve Güvenlik Kontrol Merkezi
          </Typography>
        </Toolbar>
      </AppBar>

      {/* ── Çekmeceler ── */}
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* ── İçerik ── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 4 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: "1600px",
          margin: "0 auto",
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 76 } }} />
        <Fade in timeout={600}>
          <Box>{children}</Box>
        </Fade>
      </Box>
    </Box>
  );
}
